import { STOP_PROP_TAG } from "react-data-table-component";
import type { TableColumn } from "react-data-table-component";

/** Narrowest width in characters, so short IDs and the header still fit. */
export const SOURCE_ID_MIN_CHARS = 12;

/** Widest width in characters; longer IDs wrap onto further lines. */
export const SOURCE_ID_MAX_CHARS = 40;

/** Width allowed per Source ID character at the tables' 13px font; CR numbers render at about 7.4px. */
export const SOURCE_ID_CHAR_PX = 8;

/** react-data-table-component's default left + right cell padding. */
const DATA_TABLE_CELL_PADDING_PX = 32;

interface SourceIdRow {
  source_uid?: string | null;
}

/**
 * Text width in px that fits the longest Source ID among `rows` on one line,
 * clamped to SOURCE_ID_MIN_CHARS..SOURCE_ID_MAX_CHARS characters. Excludes
 * cell padding. Px rather than `ch`, because the data table's header and body
 * cells use different font sizes and a `ch` width would differ between them.
 */
export const sourceIdTextWidthPx = (
  rows?: ReadonlyArray<SourceIdRow> | null,
): number => {
  const longest = (rows ?? []).reduce(
    (max, row) => Math.max(max, row.source_uid?.length ?? 0),
    0,
  );
  const chars = Math.min(
    SOURCE_ID_MAX_CHARS,
    Math.max(SOURCE_ID_MIN_CHARS, longest),
  );
  return chars * SOURCE_ID_CHAR_PX;
};

/**
 * The full Source ID. An ID longer than its cell wraps, breaking anywhere if
 * it has no hyphens or spaces, and is never cut off with an ellipsis.
 * `dataTag` is the react-data-table-component row-event tag: rows ignore
 * clicks and double-clicks whose target lacks it. `testId` is left off a row
 * with no Source ID, so such rows never share one.
 */
export const SourceIdText = ({
  sourceUid,
  testId,
  dataTag,
}: {
  sourceUid?: string | null;
  testId: string;
  dataTag?: string;
}) => (
  <div
    title={sourceUid ?? undefined}
    data-testid={sourceUid ? testId : undefined}
    data-tag={dataTag}
    style={{ overflowWrap: "anywhere" }}
  >
    {sourceUid}
  </div>
);

/**
 * Source ID column for the react-data-table-component audit tables, with a
 * fixed width sized to the Source IDs in `rows` (the current page's records).
 * The custom `cell` is needed because `wrap: true` keeps the library's
 * hidden overflow and ellipsis, which would still clip an ID with no break
 * points.
 */
export const sourceIdColumn = <T extends SourceIdRow>(
  rows?: ReadonlyArray<T> | null,
): TableColumn<T> => ({
  name: "Source ID",
  selector: (row) => row.source_uid ?? "",
  cell: (row) => (
    <SourceIdText
      sourceUid={row.source_uid}
      testId={`cap-audit-source-id-${row.source_uid}`}
      dataTag={STOP_PROP_TAG}
    />
  ),
  width: `${sourceIdTextWidthPx(rows) + DATA_TABLE_CELL_PADDING_PX}px`,
});
